import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteLoan,
  deletePayrollEmployeePayment,
  deletePayrollMonth,
  fetchLoanContract,
  fetchLoanInstallments,
  fetchLoans,
  fetchPayrollEmployees,
  fetchPayrollMonths,
  markLoanInstallmentsPaid,
  parseLoanContractPdf,
  parsePayrollPdf,
  recordPayrollEmployeePayment,
  saveLoan,
  savePayrollMonth,
  settlePayrollRemaining,
  type ContractParseResult,
  type ContractPdfFile,
  type EmployeePaymentDraft,
  type Loan,
  type LoanContract,
  type LoanDraft,
  type LoanInstallmentStatus,
  type LoanRequestOptions,
  type LoanWriteResult,
  type MarkInstallmentsDraft,
  type MarkInstallmentsResult,
  type Materialization,
  type ParsedPalgaleht,
  type PayrollDraft,
  type PayrollEmployeesResult,
  type PayrollMonth,
  type PayrollPdfFile,
  type PayrollWriteResult,
  type SettleRemainingResult,
} from '@/lib/loans';
import type { CashFlowEntry } from '@/lib/cashFlow';

/**
 * Query layer for Laenud & palgafond (replit.md §1.4 — all state flows through
 * one query layer with shared keys). Every write here re-materializes plain
 * cash_flow_entry rows on the server, so each mutation invalidates THREE shared
 * trees: its own roster, the `cash-flow` tree (both lenses + register + search
 * pick up the new/changed rows) and the shared `freshness` key (the write stamps
 * the `loan` sync_state row).
 */
export const loansQueryKey = ['loans'] as const;
export const payrollQueryKey = ['payroll'] as const;

export interface UseLoansResult {
  loans: Loan[];
  isLoading: boolean;
  isError: boolean;
}

export function useLoans(options?: LoanRequestOptions): UseLoansResult {
  const query = useQuery({ queryKey: loansQueryKey, queryFn: () => fetchLoans(options) });
  return { loans: query.data ?? [], isLoading: query.isLoading, isError: query.isError };
}

export interface UsePayrollMonthsResult {
  months: PayrollMonth[];
  isLoading: boolean;
  isError: boolean;
}

export function usePayrollMonths(options?: LoanRequestOptions): UsePayrollMonthsResult {
  const query = useQuery({ queryKey: payrollQueryKey, queryFn: () => fetchPayrollMonths(options) });
  return { months: query.data ?? [], isLoading: query.isLoading, isError: query.isError };
}

function useInvalidateAfterMaterialize(ownKey: readonly string[]) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ownKey });
    queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
    queryClient.invalidateQueries({ queryKey: ['freshness'] });
  };
}

export interface SaveLoanInput {
  id: string | null;
  draft: LoanDraft;
}

export function useSaveLoan(options?: LoanRequestOptions) {
  const invalidate = useInvalidateAfterMaterialize(loansQueryKey);
  return useMutation<LoanWriteResult, Error, SaveLoanInput>({
    mutationFn: ({ id, draft }) => saveLoan(draft, id, options),
    onSuccess: invalidate,
  });
}

export function useDeleteLoan(options?: LoanRequestOptions) {
  const invalidate = useInvalidateAfterMaterialize(loansQueryKey);
  return useMutation<Materialization, Error, string>({
    mutationFn: (id: string) => deleteLoan(id, options),
    onSuccess: invalidate,
  });
}

export interface SavePayrollInput {
  month: string;
  draft: PayrollDraft;
}

export function useSavePayrollMonth(options?: LoanRequestOptions) {
  const invalidate = useInvalidateAfterMaterialize(payrollQueryKey);
  return useMutation<PayrollWriteResult, Error, SavePayrollInput>({
    mutationFn: ({ month, draft }) => savePayrollMonth(month, draft, options),
    onSuccess: invalidate,
  });
}

export function useDeletePayrollMonth(options?: LoanRequestOptions) {
  const invalidate = useInvalidateAfterMaterialize(payrollQueryKey);
  return useMutation<Materialization, Error, string>({
    mutationFn: (month: string) => deletePayrollMonth(month, options),
    onSuccess: invalidate,
  });
}

/* ------------------------------------------- loan installments (maksegraafik) */

/** Shared key for one loan's schedule-settlement surface (editor Staatus column). */
export function loanInstallmentsQueryKey(loanId: string) {
  return ['loans', 'installments', loanId] as const;
}

export interface UseLoanInstallmentsResult {
  installments: LoanInstallmentStatus[];
  isLoading: boolean;
  isError: boolean;
}

/** The live settlement state of a saved loan's materialized schedule rows.
 *  `loanId` null = create mode (nothing materialized yet) — the query stays off. */
export function useLoanInstallments(
  loanId: string | null,
  options?: LoanRequestOptions,
): UseLoanInstallmentsResult {
  const query = useQuery({
    queryKey: loanInstallmentsQueryKey(loanId ?? 'none'),
    queryFn: () => fetchLoanInstallments(loanId as string, options),
    enabled: loanId !== null,
  });
  return { installments: query.data ?? [], isLoading: query.isLoading, isError: query.isError };
}

/** Bulk mark-paid settles calendar rows server-side, so it invalidates the same
 *  three shared trees as every materializing write PLUS the loan's own schedule
 *  surface (loansQueryKey is its prefix — kept explicit for clarity). */
export function useMarkLoanInstallmentsPaid(loanId: string, options?: LoanRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation<MarkInstallmentsResult, Error, MarkInstallmentsDraft>({
    mutationFn: (draft) => markLoanInstallmentsPaid(loanId, draft, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: loanInstallmentsQueryKey(loanId) });
      queryClient.invalidateQueries({ queryKey: loansQueryKey });
      queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
      queryClient.invalidateQueries({ queryKey: ['freshness'] });
    },
  });
}

/* ------------------------------------------- loan contracts (lepingu import) */

/** Shared key for one loan's stored contract block (editor + roster facts). */
export function loanContractQueryKey(loanId: string) {
  return ['loans', 'contract', loanId] as const;
}

export interface UseLoanContractResult {
  contract: LoanContract | null;
  isLoading: boolean;
  isError: boolean;
}

/** One loan's stored contract block — `loanId` null (create mode) keeps the
 *  query off. Computed loans answer null (the API's own convention). */
export function useLoanContract(
  loanId: string | null,
  options?: LoanRequestOptions,
): UseLoanContractResult {
  const query = useQuery({
    queryKey: loanContractQueryKey(loanId ?? 'none'),
    queryFn: () => fetchLoanContract(loanId as string, options),
    enabled: loanId !== null,
  });
  return { contract: query.data ?? null, isLoading: query.isLoading, isError: query.isError };
}

export interface UseLoanContractsResult {
  /** Loaded contract blocks keyed by loan id (only contract-sourced loans appear). */
  contractsByLoan: Map<string, LoanContract>;
  isLoading: boolean;
  isError: boolean;
}

/** The stored contract blocks of every contract-sourced loan in the roster —
 *  the portfolio table/summary replays THESE rows (one truth per number), so
 *  each fetch shares the same per-loan cache key as {@link useLoanContract}. */
export function useLoanContracts(loans: Loan[], options?: LoanRequestOptions): UseLoanContractsResult {
  const contractLoans = loans.filter((loan) => loan.scheduleSource === 'contract');
  return useQueries({
    queries: contractLoans.map((loan) => ({
      queryKey: loanContractQueryKey(loan.id),
      queryFn: () => fetchLoanContract(loan.id, options),
    })),
    combine: (results) => {
      const contractsByLoan = new Map<string, LoanContract>();
      results.forEach((result, index) => {
        if (result.data) {
          contractsByLoan.set(contractLoans[index].id, result.data);
        }
      });
      return {
        contractsByLoan,
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
      };
    },
  });
}

/** AI-assisted contract PDF parse — a pure read into a review prefill (writes
 *  NOTHING; the reviewed save is an ordinary {@link useSaveLoan} whose success
 *  path already invalidates the shared trees). */
export function useParseLoanContract(options?: LoanRequestOptions) {
  return useMutation<ContractParseResult, Error, ContractPdfFile>({
    mutationFn: (file: ContractPdfFile) => parseLoanContractPdf(file, options),
  });
}

/* ------------------------------------------- palgaleht import + settle pane */

/** Shared key for one month's per-employee settle surface (both lenses' detail
 *  overlay + the payroll panel read the SAME cache — L14). */
export function payrollEmployeesQueryKey(month: string) {
  return ['payroll', 'employees', month] as const;
}

/** Deterministic PDF(s) → one merged preview; a pure read (no invalidation —
 *  nothing changed). Several PDFs of one month are merged server-side. */
export function useParsePayrollPdf(options?: LoanRequestOptions) {
  return useMutation<ParsedPalgaleht, Error, PayrollPdfFile[]>({
    mutationFn: (files: PayrollPdfFile[]) => parsePayrollPdf(files, options),
  });
}

export interface UsePayrollEmployeesResult {
  data: PayrollEmployeesResult | null;
  isLoading: boolean;
  isError: boolean;
}

export function usePayrollEmployees(
  month: string,
  options?: LoanRequestOptions,
): UsePayrollEmployeesResult {
  const query = useQuery({
    queryKey: payrollEmployeesQueryKey(month),
    queryFn: () => fetchPayrollEmployees(month, options),
  });
  return { data: query.data ?? null, isLoading: query.isLoading, isError: query.isError };
}

/** Every settlement write invalidates the month's settle surface + the shared
 *  cash-flow tree (chip/register jääk) + freshness — the same three trees the
 *  materializing writes touch, PLUS the per-month employee cache. */
function useInvalidateAfterSettlement(month: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: payrollEmployeesQueryKey(month) });
    queryClient.invalidateQueries({ queryKey: payrollQueryKey });
    queryClient.invalidateQueries({ queryKey: ['cash-flow'] });
    queryClient.invalidateQueries({ queryKey: ['freshness'] });
  };
}

export interface RecordEmployeePaymentInput {
  lineId: string;
  draft: EmployeePaymentDraft;
}

export function useRecordPayrollEmployeePayment(month: string, options?: LoanRequestOptions) {
  const invalidate = useInvalidateAfterSettlement(month);
  return useMutation<CashFlowEntry, Error, RecordEmployeePaymentInput>({
    mutationFn: ({ lineId, draft }) => recordPayrollEmployeePayment(month, lineId, draft, options),
    onSuccess: invalidate,
  });
}

export function useDeletePayrollEmployeePayment(month: string, options?: LoanRequestOptions) {
  const invalidate = useInvalidateAfterSettlement(month);
  return useMutation<CashFlowEntry | null, Error, string>({
    mutationFn: (paymentId: string) => deletePayrollEmployeePayment(paymentId, options),
    onSuccess: invalidate,
  });
}

export interface SettleRemainingInput {
  idempotencyKey: string;
  paidOn?: string;
}

export function useSettlePayrollRemaining(month: string, options?: LoanRequestOptions) {
  const invalidate = useInvalidateAfterSettlement(month);
  return useMutation<SettleRemainingResult, Error, SettleRemainingInput>({
    mutationFn: ({ idempotencyKey, paidOn }) => settlePayrollRemaining(month, idempotencyKey, paidOn, options),
    onSuccess: invalidate,
  });
}
